import { handleToast } from "utils/toasts";

/**
 * The open screenplay editor's waiting edits, per project. A draft switch, a project switch
 * and leaving the editor's page save them first and only go ahead once they are saved.
 * Module scope on purpose, like `draftActivationRegistry`: the switcher sits in the project
 * header and the leave blocker at the router root, far from the editor.
 */

/** Saves the editor's waiting edits. True when nothing waits any more. */
export type SaveWaitingEdits = () => Promise<boolean>;

/** The open editor, for people who can edit it. */
export type OpenScreenplayEditor = {
  save: SaveWaitingEdits;
  /** True while an edit still needs a save, read synchronously before a navigation. */
  hasUnsavedEdits: () => boolean;
};

/**
 * What a switch or a leave may do once the save settles: go ahead, stay put (the save failed
 * or hung, and the user was told), or nothing because a later request took over.
 */
export type SaveBeforeOutcome = "proceed" | "stay" | "superseded";

/** Longest a switch waits for the editor's save before it stays put. */
export const SCREENPLAY_SAVE_WAIT_MS = 10_000;

export const SAVE_BEFORE_SWITCH_FAILED_MESSAGE =
  "Couldn't save your screenplay edits, so you stayed on this draft. Try again.";

const editorByProject = new Map<string, OpenScreenplayEditor>();
/** The latest switch or leave. Only it may act once its save settles. */
let latestRequest = 0;

/**
 * Waits for `save` for at most `ms`; `saved` is false when the time runs out or the save
 * throws. `stop` ends the wait early. The timer is always cleared.
 */
export function waitForSave(
  save: SaveWaitingEdits,
  ms = SCREENPLAY_SAVE_WAIT_MS
): { saved: Promise<boolean>; stop: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stop = () => {};
  const saved = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), ms);
    stop = () => {
      clearTimeout(timer);
      resolve(false);
    };
    save().then(resolve, () => resolve(false));
  }).finally(() => clearTimeout(timer));
  return { saved, stop };
}

export const screenplaySaveGate = {
  /** Registers the project's open editor. Call the result on unmount. */
  register(projectId: string, editor: OpenScreenplayEditor): () => void {
    editorByProject.set(projectId, editor);
    return () => {
      if (editorByProject.get(projectId) === editor) editorByProject.delete(projectId);
    };
  },

  hasOpenEditor(projectId: string): boolean {
    return editorByProject.has(projectId);
  },

  /** True when an open editor has an edit that leaving its page would lose. */
  hasUnsavedEdits(): boolean {
    return [...editorByProject.values()].some((editor) => editor.hasUnsavedEdits());
  },

  /**
   * Saves the open editor (the project's, or any when no project is given) before a switch or
   * a leave. This request becomes the latest: an earlier one still waiting gets "superseded".
   * On "stay" the editor keeps its draft and the unsaved text, its own retry keeps going, and
   * the user is told. `stop` ends the wait without a toast, for a caller that went away.
   */
  saveBefore(projectId?: string): { outcome: Promise<SaveBeforeOutcome>; stop: () => void } {
    const request = ++latestRequest;
    const editors =
      projectId === undefined
        ? [...editorByProject.values()]
        : [editorByProject.get(projectId)].filter((editor) => editor !== undefined);
    let stopped = false;
    const wait = waitForSave(async () => {
      const results = await Promise.all(editors.map((editor) => editor.save()));
      return results.every(Boolean);
    });
    const outcome = wait.saved.then((saved): SaveBeforeOutcome => {
      if (stopped || request !== latestRequest) return "superseded";
      if (!saved) {
        handleToast(SAVE_BEFORE_SWITCH_FAILED_MESSAGE, "error");
        return "stay";
      }
      return "proceed";
    });
    return {
      outcome,
      stop: () => {
        stopped = true;
        wait.stop();
      },
    };
  },
};
